import React, { useEffect, useState, useCallback, useRef } from "react";

import { Button } from "src/components/ui/button";
import { useSnackbar, type SnackbarKey } from "notistack";

import backendClient from "src/api";

/**
 * Parses a version string into its semantic version components
 * Handles both release versions (e.g., "6.5.4") and development versions (e.g., "6.5.4-29-g1576c3c")
 */
const parseVersion = (
  version: string
): { major: number; minor: number; patch: number } | null => {
  if (version === "local") return null;
  const match = version.match(/^(\d+)\.(\d+)\.(\d+)(?:-\d+-g[a-f0-9]+)?$/);
  if (!match) return null;
  return {
    major: parseInt(match[1]),
    minor: parseInt(match[2]),
    patch: parseInt(match[3]),
  };
};

type UpdateType = "major" | "minor" | null;

/**
 * Determines the type of version update (major or minor)
 */
const getUpdateType = (
  currentVersion: string,
  newVersion: string
): UpdateType => {
  const current = parseVersion(currentVersion);
  const next = parseVersion(newVersion);

  if (!current || !next) return null;

  if (next.major > current.major) return "major";
  if (next.minor > current.minor) return "minor";
  return null;
};

const VersionChecker: React.FC = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const checkingRef = useRef(false);
  const [lastChecked, setLastChecked] = useState<number | null>(null);

  const checkVersion = useCallback(async () => {
    console.debug("Checking client and server versions");
    // Prevent concurrent checks
    if (checkingRef.current) {
      return;
    }

    try {
      checkingRef.current = true;
      const currentVersion = import.meta.env.VITE_APP_VERSION || "unknown";

      if (currentVersion === "unknown") {
        return;
      }

      const { version: serverVersion } =
        await backendClient.version.getAppVersion();
      console.log(
        "Current version:",
        currentVersion,
        "Server version:",
        serverVersion
      );

      const updateType = getUpdateType(currentVersion, serverVersion);
      if (!updateType) {
        return;
      }

      const isMajorUpdate = updateType === "major";
      const message = isMajorUpdate
        ? "An update is required for the application to function normally."
        : "A newer version of the application is available.";

      const action = (snackbarId: SnackbarKey) => (
        <div className="flex flex-row gap-4 items-center">
          <Button
            size="sm"
            className="bg-background text-foreground hover:bg-muted min-w-0 px-3 py-1"
            onClick={() => window.location.reload()}
          >
            Update Now
          </Button>
          {!isMajorUpdate && (
            <Button
              variant="ghost"
              size="sm"
              className="text-background hover:bg-muted min-w-0 px-2 py-1"
              onClick={() => closeSnackbar(snackbarId)}
            >
              Later
            </Button>
          )}
        </div>
      );

      enqueueSnackbar(message, {
        variant: "warning",
        persist: true,
        preventDuplicate: true,
        anchorOrigin: { vertical: "bottom", horizontal: "center" },
        action,
      });
    } catch (error) {
      console.error("Failed to check version:", error);
    } finally {
      checkingRef.current = false;
      setLastChecked(Date.now());
    }
  }, [enqueueSnackbar, closeSnackbar, lastChecked]); // Minimal dependencies

  useEffect(() => {
    // Initial check
    checkVersion();

    // Set up interval
    const interval = setInterval(() => {
      checkVersion();
    }, 5 * 60 * 1000); // 5 Minutes

    // Cleanup
    return () => {
      clearInterval(interval);
    };
  }, []);

  return null;
};

export default VersionChecker;
